import hyperdom from 'hyperdom'
import httpism from 'httpism'
import * as gtmDataLayer from '../../../analytics/gtm/dataLayer.js'
import isScriptLoaded from 'root-core/isScriptLoaded'

class PayPalCallbacks {

  constructor ({paymentServicesUrl, environment}) {
    this.paymentServicesUrl = paymentServicesUrl
    this.onSuccessCallback = () => {}
    this.onFailureCallback = () => {}
    this.validate = () => {}
    this.hideErrors = true
    this.paypalParams = {
      env: environment === 'production' ? 'production' : 'sandbox',
      commit: true,
      payment: this.paymentCallback.bind(this),
      onAuthorize: this.onAuthorizeCallback.bind(this),
      style: {
        label: 'pay',
        size: 'medium',
        shape: 'rect',
        color: 'gold',
      },
      validate: (actions) => {
        this.payPalButtonActions = actions
        return this.payPalButtonActions.disable()
      },
      onClick: () => {
        this.hideErrors = false
        this.validate({hideErrors: this.hideErrors})
        this.refresh()
      },
    }
  }

  setPaymentParams ({sellingCompanyCode, bookingId, amount, product, shippingAddress, customer}) {
    this.sellingCompanyCode = sellingCompanyCode
    this.bookingId = bookingId
    this.amount = amount
    this.product = product
    this.shippingAddress = shippingAddress
    this.customer = customer
  }

  onSuccess (cb) {
    this.onSuccessCallback = cb
  }

  onFailure (cb) {
    this.onFailureCallback = cb
  }

  async paymentCallback () {
    const url = `${this.paymentServicesUrl}api/paypal/create-payment/${this.sellingCompanyCode}/${this.bookingId}`
    const payload = {
      amount: this.amount,
      product: this.product,
      shippingAddress: this.shippingAddress,
      customer: this.customer,
    }

    try {
      const res = await httpism.post(url, payload, {timeout: 20000})
      Object.assign(this, {requestID: res.data.requestID, requestToken: res.data.requestToken})
      return res.data.paypalToken
    } catch (err) {
      this.onFailureCallback(['We were unable to complete your purchase. Please try again with a different funding source in your PayPal account or choose another payment method.'])
      throw err
    }
  }

  async onAuthorizeCallback (data) {
    const url = `${this.paymentServicesUrl}api/paypal/execute-payment/${this.sellingCompanyCode}/${this.bookingId}`
    try {
      const payload = {payerID: data.payerID, paypalToken: data.paymentToken, requestID: this.requestID, requestToken: this.requestToken, amount: this.amount}
      const response = await httpism.post(url, payload, {timeout: 20000})
      this.onSuccessCallback({amount: Number(response.data.amount), currency: response.data.currency, customerPayPalEmail: response.data.customerPayPalEmail})
    } catch (err) {
      this.onFailureCallback(['We were unable to complete your purchase. Please try again with a different funding source in your PayPal account or choose another payment method.'])
      throw err
    }
  }

  checkValidation () {
    this.isValid = this.validate({hideErrors: this.hideErrors}).valid
    if (this.payPalButtonActions) {
      if (this.isValid) {
        this.payPalButtonActions.enable()
      } else {
        this.payPalButtonActions.disable()
      }
    }
  }
}

class PaypalButton extends PayPalCallbacks {
  onadd () {
    gtmDataLayer.activateOptimize()
    window.paypal.Button.render(this.paypalParams, 'paypal-button-container')
  }

  onupdate () {
    gtmDataLayer.activateOptimize()
  }

  render () {
    this.checkValidation()
    return <div id='paypal-button-container' class='u-margin-top-medium'></div>
  }
}

class FakePaypalButton extends PayPalCallbacks {
  constructor (options) {
    super(options)
    this.payerID = options.payerID
  }

  onadd (el) {
    // bare DOM rather than jsx to make sure fake button does not trigger hyperdom renders
    // just like the real one
    this.fakeButton = document.createElement('button')
    this.fakeButton.innerText = 'Pay with PayPal'
    el.appendChild(this.fakeButton)

    el.querySelector('button').addEventListener('click', async (e) => {
      e.preventDefault()
      this.paypalParams.onClick()

      if (this.fakeButton.className == 'enabled') {
        const paypalToken = await this.paypalParams.payment()
        await this.paypalParams.onAuthorize({
          payerID: this.payerID,
          paymentToken: paypalToken,
        })
      }
    })

    const actions = {
      enable: () => {this.fakeButton.className = 'enabled'},
      disable: () => {this.fakeButton.className = 'disabled'},
    }
    this.paypalParams.validate(actions)
  }

  render () {
    this.checkValidation()
    return <div id='paypal-button-container'></div>
  }
}

function injectScript () {
  const url = 'https://www.paypalobjects.com/api/checkout.js'
  if (!isScriptLoaded(url)) {
    const paypalScript = document.createElement('script')
    paypalScript.src = url
    document.head.appendChild(paypalScript)
  }
}

export default function ({paymentServicesUrl, fakePaypalButton, environment}) {
  if (fakePaypalButton) {
    return new FakePaypalButton({payerID: fakePaypalButton.payerID, paymentServicesUrl})
  } else {
    injectScript()
    return new PaypalButton({paymentServicesUrl, environment})
  }
}
